// @flow
// $FlowFixMe
import React, {Component, Suspense, lazy} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import {Toaster} from '@homeaway/react-toaster';
import {Unveil} from '@homeaway/react-unveil';
import {
    MesoAds,
    MesoAdsContextWithSearchParams
} from '@homeaway/react-meso-ads';
import Experiment from '@homeaway/react-experiment';
import AnalyticsDataLayer, {
    fragments as analyticsDataLayerFragments
} from '../AnalyticsDataLayer/AnalyticsDataLayer';
import FooterPlaceholder from '../AsyncFooter/FooterPlaceholder';
import HeaderBar from '../ConnectedHeader/ConnectedHeader';
import DropdownPanelOverlay from '../DropdownPanel/DropdownPanelOverlay';
import OverlayUpdateInterceptor from '../OverlayUpdateInterceptor/OverlayUpdateInterceptor';
import ButtonBar from '../ButtonBar/ButtonBar';
import Sticky from '../Sticky/Sticky';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import SearchFormExperimentWrapper from '../SearchFormExperimentWrapper/SearchFormExperimentWrapper';
import FlexibleDatesContainer from '../FlexibleDates/FlexibleDatesContainer';
import FiltersContainer from '../Filters/FiltersContainer';
import {MAP_VIEW} from '../../constants/views';
import Map from '../Map/Map';
import Results from '../Results/Results';
import ResultsAriaLiveStatus from '../ResultsAriaLiveStatus/ResultsAriaLiveStatus';
import MapHitsDataProvider from '../MapHitsDataProvider/MapHitsDataProvider';
import SearchTermCarousel from '../SearchTermCarousel/SearchTermCarousel';
import {media} from '../../utils/breakpoints';
import {FILTERS_MODAL} from '../../actions/modals';
import routerActions from '../../actions/router';
import redirectActions from '../../actions/redirect';
import viewportActions from '../../actions/viewport';
import topBannersActions from '../../actions/topBanners';
import withWindowResize from '../withWindowResize';
import withTopBanners from '../withTopBanners/withTopBanners';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import withSearchQuery from '../withSearchQuery/withSearchQuery';
import InternalTools from '../InternalTools/InternalTools';
import {isDateless} from '../../utils/searchParamsUtils';
import SeoLinkpile from '../SeoLinkpile/SeoLinkpile';
import {SearchBannerExperimentWrapper} from '../SearchBannerExperimentWrapper/SearchBannerExperimentWrapper';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import TripBoardsProviderContainer from '../TripBoardsProviderContainer/TripBoardsProviderContainer';
import {
    HA_PTA_LANDING_CLADSONSERP,
    HA_PTA_LANDING_R1ADSONSERP
} from '../../abacus/experiments';
import {PreFooterWithSeoData} from '../PreFooter/PreFooter';
import {HtmlTitleWithSeoData} from '../HtmlTitle/HtmlTitle';
import {
    SEARCH_TERM_CAROUSEL_RESULTS_MIN,
    logSearchTermCarousel
} from '../../utils/carouselLogUtils';

// Lazy Components
/* istanbul ignore next */
const AsyncFooter = lazy(() =>
    import(/* webpackChunkName: "AsyncFooter" */ '../AsyncFooter/Footer')
);

import '../../_overrides.less';
import '../../_utilities.less';
import './Application.less';
import '../../_browser.less';

import type {CallbackProps as FilteredCountDataShape} from '../FilteredCountDataProvider/FilteredCountDataProvider';
import type {Environment} from '@homeaway/environment-resolver';
import type {SearchQueryResponseData} from '../../queries/searchQuery';
import type {State as AbacusState} from '../../reducers/abacus';
import type {State as ReduxState} from '../../reducers/index';
import type {SystemData} from '../../../server/utils/systemData';
import type {SearchParamsType} from '../../reducers/searchParams';

type StateProps = {
    abacus: AbacusState,
    allowBanners: boolean,
    environment: Environment,
    filterModalOpen: boolean,
    filter: ?(string[]),
    isDatelessSearch: boolean,
    isFiltersModalShowing: boolean,
    isFullScreenMapView: boolean,
    isInternalUser: boolean,
    isLoggedIn: boolean,
    isMapShowing: boolean,
    isRedirecting: boolean,
    isXsScreenWidth: boolean,
    fullLocale: string,
    scrollY: number,
    showBrandTransitionBanner: string,
    showOverlay: boolean,
    siteName: string,
    ssr?: boolean,
    system: SystemData,
    tripBoardProviderSearchParams: {
        adultsCount?: number,
        startDate?: string,
        childrenCount?: number,
        endDate?: string,
        petIncluded?: boolean,
        searchTerm?: string
    },
    topBannersHeight: number,
    mesoAdsContextSearchParams: {
        pathname: string,
        params: SearchParamsType,
        prevParams: SearchParamsType
    }
};

type WithSearchQueryProps = {
    graphiqlUrl: string
};

type OwnProps = {
    data: SearchQueryResponseData,
    onBackToList: typeof routerActions.list,
    onResize: typeof viewportActions.sizeChanged,
    onTopBannersSizeChanged: typeof topBannersActions.sizeChanged,
    redirect: typeof redirectActions.redirect
};

export type Props = StateProps & WithSearchQueryProps & OwnProps;

const renderFilterFormWithFilteredCountData = ({
    resultsCount,
    filterGroups
}: FilteredCountDataShape) => (
    <FiltersContainer resultsCount={resultsCount} filterGroups={filterGroups} />
);

type State = {
    isMesoAdR1Loaded: boolean
};

export class App extends Component<Props, State> {
    /* istanbul ignore next */
    constructor(props: Props) {
        super(props);
        this.state = {
            isMesoAdR1Loaded: false
        };
    }

    componentDidUpdate({
        isFiltersModalShowing: prevIsFiltersModalShowing,
        isMapShowing: prevIsMapShowing
    }: Props) {
        const {
            data,
            isFiltersModalShowing,
            isFullScreenMapView,
            isMapShowing,
            onBackToList,
            scrollY,
            filter
        } = this.props;

        // Restore the scroll position if we're coming back from map view or
        // filters modal
        if (
            typeof window !== 'undefined' &&
            ((prevIsMapShowing && !isMapShowing) ||
                (prevIsFiltersModalShowing && !isFiltersModalShowing))
        ) {
            window.scrollTo(0, scrollY);
        }

        if (isFullScreenMapView && !data.loading && !data.results) {
            onBackToList();
        }

        if (data && data.results) {
            const {discoveryXploreFeeds, resultCount, geography} = data.results;
            const lbsId = geography ? geography.lbsId : '';

            logSearchTermCarousel(
                discoveryXploreFeeds,
                resultCount,
                lbsId,
                filter
            );
        }
    }

    onMesoAdR1Loaded = () => {
        this.setState({
            isMesoAdR1Loaded: true
        });
    };

    onMesoAdR1Failed = () => {
        this.setState({
            isMesoAdR1Loaded: false
        });
    };

    render() {
        const {
            abacus,
            allowBanners,
            data,
            filterModalOpen,
            graphiqlUrl,
            isDatelessSearch,
            isFullScreenMapView,
            isInternalUser,
            isMapShowing,
            isRedirecting,
            isLoggedIn,
            isXsScreenWidth,
            onBackToList,
            redirect,
            showOverlay,
            siteName,
            system,
            topBannersHeight,
            showBrandTransitionBanner,
            tripBoardProviderSearchParams,
            environment,
            mesoAdsContextSearchParams
        } = this.props;

        const {loading, results, error, requestmarker} = data;

        const {
            destination,
            internalTools,
            listings,
            resultCount,
            pageCount,
            page,
            propertyRedirectUrl,
            destinationMessage,
            typeaheadSuggestion,
            discoveryXploreFeeds,
            topRecentReviews,
            geography
        } = results || {};

        if (!loading && propertyRedirectUrl) {
            redirect(propertyRedirectUrl);
        }

        const isResultsError = !loading && !results;
        const isRedirectingToList = isResultsError && isFullScreenMapView;
        const mapButtonDisabled = isXsScreenWidth && isResultsError;
        const classes = classNames('Application', siteName, {
            'Application--overlay': showOverlay,
            'Application--fullScreenMap': isFullScreenMapView
        });

        const applicationMapClasses = classNames('Application__map', {
            'Application__map--fullScreen': isFullScreenMapView
        });

        const applicationWithAdsClasses = classNames({
            'Application__with--R1ADS':
                (abacus[HA_PTA_LANDING_R1ADSONSERP] || {}).value === 1 &&
                this.state.isMesoAdR1Loaded,
            'Application__with--CLADS':
                (abacus[HA_PTA_LANDING_CLADSONSERP] || {}).value > 0
        });

        const topBannerMargin = {marginTop: topBannersHeight};
        const applicationMapStyles = allowBanners ? topBannerMargin : {};
        const isLoadingOrRedirecting =
            loading || isRedirecting || isRedirectingToList;

        const showBackToList = isFullScreenMapView;

        const hasResults = !!(listings && listings.length);

        const adlFragment =
            (!isLoadingOrRedirecting &&
                !!results &&
                filterGraphQlAnywhere(
                    analyticsDataLayerFragments.results,
                    results
                )) ||
            null;
        return (
            <div className={classes} style={topBannerMargin}>
                <AnalyticsDataLayer
                    results={adlFragment}
                    requestmarker={requestmarker}
                />
                <header
                    className="Application__header"
                    data-wdio="HeaderBarContainer"
                >
                    <HtmlTitleWithSeoData
                        results={results}
                        resultsLoading={loading}
                        page={page}
                        pageCount={pageCount}
                    />

                    <HeaderBar
                        internalTools={
                            isInternalUser ? (
                                <InternalTools
                                    searchServiceUrl={
                                        internalTools &&
                                        internalTools.searchServiceUrl
                                    }
                                    graphiqlUrl={graphiqlUrl}
                                    environment={environment}
                                />
                            ) : null
                        }
                        showBrandTransitionBanner={showBrandTransitionBanner}
                    />
                    <SearchFormExperimentWrapper />
                </header>
                {isXsScreenWidth && (
                    <Sticky
                        className="Application__stickyButtonBarContainer"
                        top={0}
                    >
                        {({isSticky}) => (
                            <section
                                className={classNames(
                                    'Application__buttonBar Application__section--padded',
                                    {
                                        'Application__buttonBar--bordered': !isSticky,
                                        'Application__buttonBar--shadowed': isSticky
                                    }
                                )}
                            >
                                <ButtonBar
                                    mapButtonDisabled={mapButtonDisabled}
                                />
                            </section>
                        )}
                    </Sticky>
                )}
                <MesoAdsContextWithSearchParams
                    brandId={system.brandId}
                    gaiaId={geography && geography.gaiaId}
                    havId={system.havId}
                    locale={system.locale}
                    searchParams={mesoAdsContextSearchParams}
                >
                    <div className="Application__container">
                        {!isXsScreenWidth && (
                            <section className="Application__filterBar">
                                <FiltersContainer
                                    searchResultsLoading={
                                        isLoadingOrRedirecting
                                    }
                                />
                            </section>
                        )}
                        <div
                            id="Application__resultsViewport"
                            className="Application__resultsViewport"
                        >
                            <OverlayUpdateInterceptor>
                                <div
                                    className={classNames(
                                        'Application__resultsColumn',
                                        applicationWithAdsClasses
                                    )}
                                >
                                    <section className="Application__results Application__section--padded">
                                        <h1 className="sr-only">
                                            <FormattedMessage
                                                id="appSearchResultsSection"
                                                defaultMessage="Search results"
                                            />
                                        </h1>
                                        <ErrorBoundary>
                                            {!isLoadingOrRedirecting &&
                                                !!(
                                                    typeaheadSuggestion &&
                                                    pageCount &&
                                                    pageCount > 0 &&
                                                    resultCount &&
                                                    resultCount >=
                                                        SEARCH_TERM_CAROUSEL_RESULTS_MIN &&
                                                    discoveryXploreFeeds &&
                                                    discoveryXploreFeeds.results
                                                        .length > 0
                                                ) && (
                                                    <section className="Application__searchTermCarousel">
                                                        <SearchTermCarousel
                                                            discoveryXploreFeeds={
                                                                discoveryXploreFeeds
                                                            }
                                                            typeaheadSuggestion={
                                                                typeaheadSuggestion
                                                            }
                                                        />
                                                    </section>
                                                )}
                                            {!isLoadingOrRedirecting &&
                                                destinationMessage && (
                                                    <SearchBannerExperimentWrapper
                                                        destinationMessage={
                                                            destinationMessage
                                                        }
                                                        hasResults={hasResults}
                                                    />
                                                )}
                                            <ResultsAriaLiveStatus
                                                isLoading={
                                                    isLoadingOrRedirecting
                                                }
                                                pageNumber={
                                                    results && results.page
                                                }
                                            />

                                            {!isDatelessSearch && (
                                                <FlexibleDatesContainer
                                                    loading={
                                                        isLoadingOrRedirecting
                                                    }
                                                />
                                            )}

                                            <Results
                                                error={error}
                                                results={results}
                                                loading={isLoadingOrRedirecting}
                                                allowUpdate={
                                                    !isFullScreenMapView
                                                }
                                            />
                                        </ErrorBoundary>
                                    </section>
                                    <section id="Application__preFooter">
                                        <PreFooterWithSeoData
                                            results={results}
                                            resultsLoading={loading}
                                            topRecentReviews={topRecentReviews}
                                        />
                                    </section>
                                    <section
                                        className="Application__footer"
                                        role="contentinfo"
                                        data-wdio="FooterBar"
                                    >
                                        <Breadcrumb destination={destination} />
                                        <SeoLinkpile
                                            results={results}
                                            resultsLoading={loading}
                                        />
                                        <ErrorBoundary label="AsyncFooter">
                                            <Unveil threshold={400}>
                                                <Suspense
                                                    fallback={FooterPlaceholder}
                                                >
                                                    <AsyncFooter />
                                                </Suspense>
                                            </Unveil>
                                        </ErrorBoundary>
                                    </section>
                                </div>
                                <Experiment
                                    name={HA_PTA_LANDING_R1ADSONSERP}
                                    variant={Experiment.ON}
                                >
                                    <MesoAds
                                        slot={'R1'}
                                        onAdLoadSuccess={this.onMesoAdR1Loaded}
                                        onAdLoadFailed={this.onMesoAdR1Failed}
                                        style={applicationMapStyles}
                                    />
                                </Experiment>
                                <section
                                    className={applicationMapClasses}
                                    style={applicationMapStyles}
                                >
                                    <MapHitsDataProvider
                                        {...{
                                            loading: isLoadingOrRedirecting,
                                            onBackClick:
                                                showBackToList && onBackToList,
                                            options: {
                                                zoomControl:
                                                    isMapShowing &&
                                                    !showBackToList /* zoom conflicts with back to list */
                                            },
                                            render: (p) => <Map {...p} />,
                                            results,
                                            shouldShowFiltersButton: isXsScreenWidth,
                                            showBackToList,
                                            siteName,
                                            allowUpdate: isMapShowing
                                        }}
                                    />
                                </section>
                            </OverlayUpdateInterceptor>
                            <DropdownPanelOverlay />
                        </div>
                    </div>
                </MesoAdsContextWithSearchParams>

                {isXsScreenWidth && filterModalOpen && (
                    <section className="Application__filterForm">
                        <FilteredCountDataProvider
                            searchResultsLoading={isLoadingOrRedirecting}
                            children={renderFilterFormWithFilteredCountData}
                        />
                    </section>
                )}
                <OverlayUpdateInterceptor>
                    <Toaster />
                    <TripBoardsProviderContainer
                        actionLocation={'search'}
                        siteName={siteName}
                        tripBoardProviderSearchParams={
                            tripBoardProviderSearchParams
                        }
                        isLoggedIn={isLoggedIn}
                    />
                </OverlayUpdateInterceptor>
            </div>
        );
    }
}

export const mapStateToProps = (state: ReduxState): StateProps => {
    let {
        modals,
        searchParams,
        system,
        viewport: {breakpoint, scrollY},
        topBanners: {height: topBannersHeight},
        abacus,
        redirect: {isRedirecting},
        overlay: {showOverlay},
        user: {isLoggedIn}
    } = state;

    const isXsScreenWidth = !media(breakpoint, {
        minWidth: 'SM_MIN'
    });

    const isSmScreenWidth =
        !media(breakpoint, {
            minWidth: 'MD_MIN'
        }) && !isXsScreenWidth;

    const isMdScreenWidth = media(breakpoint, {
        minWidth: 'MD_MIN'
    });

    const {
        adultsCount,
        arrival: startDate,
        childrenCount,
        departure: endDate,
        filter,
        modal,
        petIncluded,
        q: searchTerm,
        ssr,
        view
    } = searchParams.params;

    const isFiltersModalShowing = modal === FILTERS_MODAL;

    const isFullScreenMapView =
        view === MAP_VIEW && !media(breakpoint, {minWidth: 'MD_MIN'});

    const isMapShowing =
        isFullScreenMapView || media(breakpoint, {minWidth: 'MD_MIN'});

    const allowBanners =
        (isFullScreenMapView && !isSmScreenWidth) ||
        (system.isDesktop && isMdScreenWidth);

    const isInternalUser = system.isInternalUser;

    return {
        abacus,
        environment: system.environment,
        allowBanners,
        isDatelessSearch: isDateless(searchParams.params),
        isFiltersModalShowing,
        isFullScreenMapView,
        isInternalUser,
        isLoggedIn,
        isMapShowing,
        isRedirecting,
        isXsScreenWidth,
        filter,
        filterModalOpen: modals.FILTERS.isOpen,
        fullLocale: system.fullLocale,
        topBannersHeight,
        scrollY,
        showOverlay,
        siteName: system.siteName,
        ssr: !!ssr,
        tripBoardProviderSearchParams: {
            startDate,
            endDate,
            adultsCount,
            petIncluded,
            searchTerm,
            childrenCount
        },
        showBrandTransitionBanner: system.transitionBanner,
        system,
        mesoAdsContextSearchParams: {
            pathname: searchParams.pathname,
            params: searchParams.params,
            prevParams: searchParams.prevParams
        }
    };
};

const mapDispatchToProps = {
    onBackToList: routerActions.list,
    onResize: viewportActions.sizeChanged,
    onTopBannersSizeChanged: topBannersActions.sizeChanged,
    redirect: redirectActions.redirect
};

export const applicationCompose = (ui: any) =>
    compose(
        // Flow wants a type parameter passed for withSearchQuery's
        // OwnProps, but I don't know how to annotate that when passing
        // withSearchQuery as part of a compose pipeline.
        //
        // withSearchQuery must come first or else its `shouldComponentUpdate`
        // will skip renders when Redux props change.
        // $FlowFixMe
        withSearchQuery,
        connect(mapStateToProps, mapDispatchToProps),
        withWindowResize,
        withTopBanners
    )(ui);

const ApplicationComponent = applicationCompose(App);
const SERP_APPLICATION_CRASHED = 'SERP application crashed';

export default function Application() {
    return (
        <ErrorBoundary errorName={SERP_APPLICATION_CRASHED}>
            <ApplicationComponent />
        </ErrorBoundary>
    );
}
